import { useTranslation } from "react-i18next"

import type { FairUseOverviewFilter, FairUseSummary } from "@/api/admin/fair-use"

import { Search } from "@/components/icons"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

type FairUseFiltersProps = {
  summary: FairUseSummary
  filter: FairUseOverviewFilter
  onFilterChange: (filter: FairUseOverviewFilter) => void
  search: string
  onSearchChange: (search: string) => void
  sort: string
  onSortChange: (sort: string) => void
  partial?: boolean
  directory?: boolean
}

export function FairUseFilters({
  summary,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  sort,
  onSortChange,
  partial,
  directory,
}: FairUseFiltersProps) {
  const { t } = useTranslation()

  // `undefined` is not 0: a backend that does not count non-default plans yet
  // must leave the chip unnumbered rather than claim there are none.
  const chips: Array<{ value: FairUseOverviewFilter; label: string; count?: number }> = [
    {
      value: "all",
      label: t("adminDashboard.fairUse.ops.filters.all", "All"),
      count: summary.users,
    },
    {
      value: "pending",
      label: t("adminDashboard.fairUse.ops.filters.pending", "Requests waiting"),
      count: summary.pending,
    },
    {
      value: "near",
      label: t("adminDashboard.fairUse.ops.filters.near", "Near their budget"),
      count: summary.near_cap,
    },
    {
      value: "custom",
      label: t("adminDashboard.fairUse.ops.filters.custom", "Custom plan"),
      count: summary.custom_plan,
    },
    {
      value: "blocked",
      // Same rows either way; only "Stopped" is untrue while limits are off.
      label: summary.enabled
        ? t("adminDashboard.fairUse.ops.filters.blocked", "Stopped")
        : t("adminDashboard.fairUse.ops.filters.overUnenforced", "Over budget"),
      count: summary.stopped,
    },
  ]

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        aria-label={t("adminDashboard.fairUse.ops.filters.groupLabel", "Filter")}
        className="flex flex-wrap gap-2"
        role="group"
      >
        {chips.map((chip) => (
          <button
            key={chip.value}
            aria-pressed={filter === chip.value}
            type="button"
            className={cn(
              "focus-visible:ring-ring inline-flex h-10 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors focus-visible:ring-1 focus-visible:outline-none",
              filter === chip.value ? "bg-accent border-transparent" : "hover:bg-accent/40"
            )}
            onClick={() => onFilterChange(chip.value)}
          >
            {chip.label}
            {chip.count != null && (
              <span className="text-muted-foreground font-normal tabular-nums">{chip.count}</span>
            )}
          </button>
        ))}
      </div>
      <Select value={sort} onValueChange={onSortChange}>
        <SelectTrigger
          className="h-10 w-full sm:w-48"
          aria-label={
            directory
              ? t("adminDashboard.peopleDirectory.sortUsers", "Sort users")
              : t("adminDashboard.tableControls.loadedSort", "Sort loaded users")
          }
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="original">
            {t("adminDashboard.tableControls.original", "Default order")}
          </SelectItem>
          <SelectItem value="cost-desc">
            {t("adminDashboard.tableControls.costDesc", "Spend: high to low")}
          </SelectItem>
          <SelectItem value="cost-asc">
            {t("adminDashboard.tableControls.costAsc", "Spend: low to high")}
          </SelectItem>
          <SelectItem value="name-asc">
            {t("adminDashboard.tableControls.nameAsc", "Name: A–Z")}
          </SelectItem>
          <SelectItem value="name-desc">
            {t("adminDashboard.tableControls.nameDesc", "Name: Z–A")}
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="relative ml-auto w-full sm:w-56">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
        <Input
          aria-label={t("adminDashboard.fairUse.ops.filters.searchLabel", "Search people")}
          className="h-10 pl-8"
          placeholder={t("adminDashboard.fairUse.ops.filters.search", "Search people…")}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      {partial && (
        <p className="text-muted-foreground w-full text-xs">
          {t(
            "adminDashboard.tableControls.loadedHint",
            "Sorting applies to loaded users. Load more to include the rest."
          )}
        </p>
      )}
    </div>
  )
}
